// Composer for a scheduled "Nachfrage an Kollegen" (auto follow-up email).
// Used standalone AND task-bound (pass taskId / personalTaskId for context).

import { useMemo, useState } from "preact/hooks";
import { Send, Sparkles } from "lucide-preact";
import type { FollowUpRecurrence } from "@juuplan/shared";
import { Modal } from "./Modal";
import { useUsers } from "../store/users";
import { useMe } from "../lib/auth";
import { createFollowUp, aiDraftFollowUp } from "../lib/api";
import { toastError, toastSuccess } from "../lib/toast";

import { Button } from "./ui/Button";

interface Props {
  onClose: () => void;
  onCreated?: () => void;
  taskId?: number;
  personalTaskId?: number;
  /** Optional human label for the linked item, shown as a hint. */
  contextLabel?: string;
}

/** Format a Date as a value for <input type="datetime-local"> (local time). */
function toLocalInput(d: Date): string {
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function FollowUpComposer(props: Props): preact.JSX.Element {
  const { onClose, onCreated, taskId, personalTaskId, contextLabel } = props;
  const users = useUsers();
  const me = useMe();

  const defaultWhen = useMemo(() => toLocalInput(new Date(Date.now() + 3600_000)), []);
  const minWhen = useMemo(() => toLocalInput(new Date()), []);
  const initialSubject = contextLabel ? `Nachfrage: ${contextLabel}` : "";
  const [recipientId, setRecipientId] = useState<number | null>(null);
  const [subject, setSubject] = useState(initialSubject);
  const [message, setMessage] = useState("");
  const [when, setWhen] = useState(defaultWhen);
  const [recurrence, setRecurrence] = useState<FollowUpRecurrence>("none");
  const [until, setUntil] = useState("");
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);

  // AI drafting is offered only with a task/list context AND when AI is enabled.
  const canDraft = Boolean((taskId || personalTaskId) && me.user?.ai_enabled);

  const draftWithAi = async (): Promise<void> => {
    setDrafting(true);
    try {
      const res = await aiDraftFollowUp(
        personalTaskId ? { personal_task_id: personalTaskId } : { task_id: taskId! }
      );
      if (res.subject) setSubject(res.subject);
      if (res.message) setMessage(res.message);
      if (!res.subject && !res.message) toastError("KI lieferte keinen Entwurf");
    } catch (err) {
      toastError((err as Error)?.message ?? "KI-Entwurf fehlgeschlagen");
    } finally {
      setDrafting(false);
    }
  };

  const candidates = useMemo(
    () => users.items.filter((u) => u.id !== me.user?.id),
    [users.items, me.user?.id]
  );

  const input =
    "w-full rounded-md border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2 text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]";
  const label = "block text-sm font-medium text-[color:var(--color-on-surface)] mb-1";

  // Toast + focus the offending field so the error is actionable.
  const fail = (msg: string, fieldId: string): void => {
    toastError(msg);
    document.getElementById(fieldId)?.focus();
  };

  const submit = async (): Promise<void> => {
    if (!recipientId) {
      fail("Bitte einen Empfänger wählen", "fu-recipient");
      return;
    }
    if (!subject.trim()) {
      fail("Bitte einen Betreff eingeben", "fu-subject");
      return;
    }
    if (!message.trim()) {
      fail("Bitte eine Nachricht eingeben", "fu-message");
      return;
    }
    const dt = new Date(when);
    if (Number.isNaN(dt.getTime())) {
      fail("Ungültiger Zeitpunkt", "fu-when");
      return;
    }
    if (recurrence !== "none" && until && until < when.slice(0, 10)) {
      fail(`„Bis" darf nicht vor dem Zeitpunkt liegen`, "fu-until");
      return;
    }

    setSaving(true);
    try {
      await createFollowUp({
        recipient_user_id: recipientId,
        subject: subject.trim(),
        message: message.trim(),
        remind_at: dt.toISOString(),
        recurrence,
        recurrence_until: recurrence !== "none" && until ? until : null,
        ...(taskId ? { task_id: taskId } : {}),
        ...(personalTaskId ? { personal_task_id: personalTaskId } : {}),
      });
      toastSuccess("Nachfrage geplant");
      onCreated?.();
      onClose();
    } catch (err) {
      toastError((err as Error)?.message ?? "Planen fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Nachfrage an Kollegen planen"
      onClose={onClose}
      size="md"
      dirty={Boolean(recipientId) || message.trim() !== "" || subject !== initialSubject}
      footer={
        <div class="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            class="rounded-md px-3 py-2 text-sm text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-on-surface)]"
          >
            Abbrechen
          </button>
          <Button
            variant="primary"
            size="lg"
            type="button"
            onClick={() => void submit()}
            disabled={saving}
          >
            <Send size={16} aria-hidden="true" />
            {saving ? "Plane…" : "Planen"}
          </Button>
        </div>
      }
    >
      <div class="space-y-4">
        {contextLabel && (
          <p class="text-xs text-[color:var(--color-on-surface-variant)]">
            Bezug: <span class="font-medium">{contextLabel}</span>
          </p>
        )}

        {canDraft && (
          <button
            type="button"
            onClick={() => void draftWithAi()}
            disabled={drafting}
            class="inline-flex items-center gap-1.5 rounded-md border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2 text-sm text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container)] disabled:opacity-50"
          >
            <Sparkles size={16} aria-hidden="true" />
            {drafting ? "KI schreibt…" : "KI: Mail aus Kontext vorschreiben"}
          </button>
        )}

        <div>
          <label class={label} for="fu-recipient">
            Empfänger <span class="text-[color:var(--color-error)]" aria-hidden="true">*</span>
          </label>
          <select
            id="fu-recipient"
            class={input}
            required
            autoFocus
            value={recipientId ?? ""}
            onChange={(e) =>
              setRecipientId(Number((e.target as HTMLSelectElement).value) || null)
            }
          >
            <option value="">— Kollegen wählen —</option>
            {candidates.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} ({u.email})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label class={label} for="fu-subject">
            Betreff <span class="text-[color:var(--color-error)]" aria-hidden="true">*</span>
          </label>
          <input
            id="fu-subject"
            class={input}
            required
            value={subject}
            maxLength={200}
            onInput={(e) => setSubject((e.target as HTMLInputElement).value)}
          />
        </div>

        <div>
          <label class={label} for="fu-message">
            Nachricht <span class="text-[color:var(--color-error)]" aria-hidden="true">*</span>
          </label>
          <textarea
            id="fu-message"
            class={`${input} min-h-[96px] resize-y`}
            required
            value={message}
            maxLength={5000}
            onInput={(e) => setMessage((e.target as HTMLTextAreaElement).value)}
          />
        </div>

        <div>
          <label class={label} for="fu-when">Zeitpunkt</label>
          <input
            id="fu-when"
            type="datetime-local"
            class={input}
            min={minWhen}
            value={when}
            onInput={(e) => setWhen((e.target as HTMLInputElement).value)}
          />
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class={label} for="fu-recurrence">Wiederholung</label>
            <select
              id="fu-recurrence"
              class={input}
              value={recurrence}
              onChange={(e) =>
                setRecurrence((e.target as HTMLSelectElement).value as FollowUpRecurrence)
              }
            >
              <option value="none">Einmalig</option>
              <option value="daily">Täglich</option>
              <option value="weekly">Wöchentlich</option>
            </select>
          </div>
          {recurrence !== "none" && (
            <div>
              <label class={label} for="fu-until">Bis (optional)</label>
              <input
                id="fu-until"
                type="date"
                class={input}
                min={when.slice(0, 10)}
                value={until}
                onInput={(e) => setUntil((e.target as HTMLInputElement).value)}
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
